/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { unstable_batchedUpdates } from 'react-dom';
import { useContext, useCallback, useRef, useEffect } from 'react';
import { WorkbenchContext } from '@/contexts/WorkbenchContext';
import { ApiFragment, FragmentStatus } from '@/types/workbench';
import { useGetLastFragment } from '@/adapters/workbentch';
import { useLastSegmentEndTime } from '@/hooks/useLastSegmentEndTime';

export interface FragmentLoader {
  EXTERNAL_CALLBACK_onNewFragments(last: number, socketId: string): void;
  EXTERNAL_CALLBACK_onFragmentStatus(status: FragmentStatus): void;
  handleFragmentsRebuild(): void;
}

export function useFragments(speakerId?: string): FragmentLoader {
  const { fragments, setFragments } = useContext(WorkbenchContext);
  const { mutateAsync: getFragmentsByWorker } = useGetLastFragment();
  const lastSegmentEndTimeMs = useLastSegmentEndTime();

  // Refs so callbacks can always read the latest value without being re-created
  const fragmentsRef = useRef<ApiFragment[]>(fragments);
  const lastSegmentEndTimeMsRef = useRef<number>(lastSegmentEndTimeMs);

  useEffect(() => {
    fragmentsRef.current = fragments;
  }, [fragments]);

  useEffect(() => {
    lastSegmentEndTimeMsRef.current = lastSegmentEndTimeMs;
  }, [lastSegmentEndTimeMs]);

  const handleFragmentsRebuild = useCallback((): void => {
    setFragments([]);
  }, [setFragments]);

  const loadFragmentsFrom = useCallback(
    async (workerId: string, __fragments: ApiFragment[], last: number): Promise<ApiFragment[]> => {
      const loadFrom: number = last - 1; //reloading last fragment

      const lastKnown =
        __fragments.length === 0
          ? -999
          : __fragments.reduce((prev, current) =>
              prev && prev.sequenceNumber > current.sequenceNumber ? prev : current,
            ).sequenceNumber;

      const pointer = Math.min(lastKnown, loadFrom);
      const apiFragments = (await getFragmentsByWorker({
        workerId,
        pointer,
        speakerId,
      })) as unknown as ApiFragment[];

      const currentLastSegmentEndTimeMs = lastSegmentEndTimeMsRef.current;

      const convertedFragments: ApiFragment[] = apiFragments.map((f) => {
        // Clips come in seconds, segments come in centiseconds
        const isClip = f.type === 'clip';
        const timeStart = isClip ? f.timeStart * 100 : f.timeStart;
        const timeStartAdjusted = isClip ? f.timeStartAdjusted * 100 : f.timeStartAdjusted;

        // Convert timeEnd, defaulting to video duration if null
        // Video duration is in milliseconds, convert to centiseconds (divide by 10)
        const videoDurationCs =
          currentLastSegmentEndTimeMs > 0 ? Math.trunc(currentLastSegmentEndTimeMs / 10) : null;
        const timeEnd =
          isClip && f.timeEnd ? f.timeEnd * 100 : f.timeEnd !== null ? f.timeEnd : videoDurationCs;
        const timeEndAdjusted =
          isClip && f.timeEndAdjusted
            ? f.timeEndAdjusted * 100
            : f.timeEndAdjusted !== null
            ? f.timeEndAdjusted
            : videoDurationCs;

        return {
          ...f,
          timeStart,
          timeEnd,
          timeStartAdjusted,
          timeEndAdjusted,
          color: '#1E1541',
          disabled: f.isDisabled,
          locked: f.isLocked,
          renderStatus: f.renderStatus,
          type: f.type,
        };
      });

      const idFragments = new Map(__fragments.map((f) => [f.id, f]));
      convertedFragments.forEach((f) => idFragments.set(f.id, f));
      const result = Array.from(idFragments.values());

      return result.sort((a, b) => a.timeStartAdjusted - b.timeStartAdjusted);
    },
    [getFragmentsByWorker, speakerId],
  );

  /**
   * Bullshit name for bullshit "feature". For future maintainers: making it VERY OBVIOUS that this
   * is a "special needs" kid which needs to be treated very carefully.
   *
   * https://github.com/pmndrs/zustand/issues/302 - more info here
   *
   * As React is very prone to creating zombie children when updating state from outside React, the
   * update needs to be (a) batched, and (b) old state loaded within the batch.
   * @param last
   * @constructor
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const EXTERNAL_CALLBACK_onNewFragments = useCallback(
    (last: number, socketId: string): void => {
      unstable_batchedUpdates(() => {
        // Read latest fragments from ref to avoid stale closure without adding fragments to deps
        const __currentFragments = fragmentsRef.current;
        if (!__currentFragments || !Array.isArray(__currentFragments)) {
          return;
        }

        loadFragmentsFrom(socketId, __currentFragments, last)
          .then((result) => {
            if (isMountedRef.current) setFragments(result);
          })
          .catch((err) => {
            if (isMountedRef.current) console.error(err);
          });
      });
    },
    [loadFragmentsFrom, setFragments],
  );

  const EXTERNAL_CALLBACK_onFragmentStatus = useCallback((status: FragmentStatus): void => {
    // const f: ApiFragment | undefined = fragments.find((f: ApiFragment) => f.id === status.fragment_id);
    console.log('Updating selected region', status);
    // if (!f) {
    //   return;
    // }
    // f.renderStatus = status.status;
    // const selected = selectedRegions;

    // console.log('Selected regions: ', selected, +(selected[0]?.id || '') === f.id);
    // const partial: any = { fragments: [...fragments] };
    // if (selected.length === 1 && +(selected[0].id || '') === f.id) {
    //     selected[0].fragmentStatus = status;
    //     partial.selectedRegions = selected;
    //     partial['selectedRegions'] = selected;
    // }
    // OutOfContextUpdate(storeApi, partial);
  }, []);

  return {
    EXTERNAL_CALLBACK_onNewFragments,
    EXTERNAL_CALLBACK_onFragmentStatus,
    handleFragmentsRebuild,
  };
}
